import React, { useEffect, useState } from 'react';
import { Clock, ChevronLeft, ChevronRight, X, ArrowUpDown, ArrowLeft, Check, Pencil, Trash2, Info } from 'lucide-react';
interface ActivityEntry {
  action: string;
  user: string;
  timestamp: string;
}
interface Webhook {
  id: string;
  name: string;
  url: string;
  type: string;
  status: 'Active' | 'Inactive';
  events: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  activity: ActivityEntry[];
}
const AVAILABLE_EVENTS = ['Client tokenisation status', 'Transaction change', 'Tokenisation status', 'Payment status', 'Refund status', 'Dispute status', 'Settlement status'];
function formatDate(iso: string) {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  const secs = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}:${secs}`;
}
/* ── Centralised style tokens (mapped from theme/tokens.css) ── */
const styles = {
  // Buttons
  btnPrimary: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]',
  btnDanger: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--colors-error-default)] hover:bg-[var(--colors-error-dark)] focus:outline-none focus:ring-2 focus:ring-red-300',
  btnGhost: 'text-sm text-[var(--colors-text-secondary)] bg-[var(--colors-background-card)] border border-[var(--colors-border-default)] rounded-md hover:bg-[var(--colors-neutral-50)] transition-colors',
  // Cards
  card: 'bg-[var(--colors-background-card)] border border-[var(--colors-border-light)] rounded-lg shadow-[var(--shadows-sm)]',
  // Text
  heading: 'text-[var(--colors-text-primary)] font-semibold',
  subtext: 'text-sm text-[var(--colors-text-tertiary)]',
  label: 'block text-sm font-medium text-[var(--colors-text-secondary)] mb-1',
  // Inputs
  input: 'w-full border border-[var(--colors-border-default)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--colors-border-focus)] bg-[var(--colors-background-card)]',
  // Badges
  badgeActive: 'bg-[var(--colors-success-light)] text-[var(--colors-success-dark)]',
  badgeInactive: 'bg-[var(--colors-neutral-100)] text-[var(--colors-neutral-600)]',
  badgeTypeGlobal: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)]',
  badgeTypeOther: 'bg-[var(--colors-primary-100)] text-[var(--colors-primary-800)]',
  badgeBase: 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium',
  // Table
  th: 'px-6 py-3 font-medium text-[var(--colors-text-secondary)] cursor-pointer hover:bg-[var(--colors-neutral-50)]',
  // Misc
  eventChip: 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-[var(--colors-neutral-100)] text-[var(--colors-text-secondary)] border border-[var(--colors-border-light)]',
  overlay: 'fixed inset-0 bg-[var(--colors-background-overlay)] flex items-center justify-center z-[var(--zindex-modal)]'
} as const;
type ViewMode = 'list' | 'add' | 'edit';
export function WebhookEndpoints() {
  const [globalWebhookUrl, setGlobalWebhookUrl] = useState('');
  const [globalUpdateStatus, setGlobalUpdateStatus] = useState<'idle' | 'success'>('idle');
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null);
  const [deleteModalWebhook, setDeleteModalWebhook] = useState<Webhook | null>(null);
  const [infoModalWebhook, setInfoModalWebhook] = useState<Webhook | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'Global',
    events: [] as string[]
  });
  useEffect(() => {
    if (globalUpdateStatus === 'success') {
      const timer = setTimeout(() => setGlobalUpdateStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
  }, [globalUpdateStatus]);
  const handleGlobalUpdate = () => {
    setGlobalUpdateStatus('success');
  };
  const handleGlobalRemove = () => {
    if (window.confirm('Are you sure you want to remove the global webhook?')) {
      setGlobalWebhookUrl('');
    }
  };
  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      type: 'Global',
      events: []
    });
  };
  const openAddView = () => {
    resetForm();
    setViewMode('add');
  };
  const openEditView = (webhook: Webhook) => {
    setFormData({
      name: webhook.name,
      url: webhook.url,
      type: webhook.type,
      events: [...webhook.events]
    });
    setEditingWebhookId(webhook.id);
    setViewMode('edit');
  };
  const handleAddWebhook = () => {
    if (!formData.name || !formData.url || formData.events.length === 0) return;
    const now = new Date().toISOString();
    const newWebhook: Webhook = {
      id: Math.random().toString(36).substring(2, 18),
      name: formData.name,
      url: formData.url,
      type: formData.type,
      status: 'Active',
      events: formData.events,
      createdAt: now,
      updatedAt: now,
      createdBy: 'admin@maybank.com.my',
      activity: [{
        action: 'Webhook created',
        user: 'admin@maybank.com.my',
        timestamp: now
      }]
    };
    setWebhooks([...webhooks, newWebhook]);
    resetForm();
    setViewMode('list');
  };
  const handleSaveEdit = () => {
    if (!formData.name || !formData.url || formData.events.length === 0) return;
    const now = new Date().toISOString();
    setWebhooks(webhooks.map(wh => wh.id === editingWebhookId ? {
      ...wh,
      name: formData.name,
      url: formData.url,
      type: formData.type,
      events: formData.events,
      updatedAt: now,
      activity: [...wh.activity, {
        action: 'Webhook updated',
        user: 'admin@maybank.com.my',
        timestamp: now
      }]
    } : wh));
    setEditingWebhookId(null);
    resetForm();
    setViewMode('list');
  };
  const handleDeleteWebhook = (webhook: Webhook) => {
    setWebhooks(webhooks.filter(wh => wh.id !== webhook.id));
    setDeleteModalWebhook(null);
  };
  const toggleEvent = (event: string) => {
    if (formData.events.includes(event)) {
      setFormData({
        ...formData,
        events: formData.events.filter(e => e !== event)
      });
    } else {
      setFormData({
        ...formData,
        events: [...formData.events, event]
      });
    }
  };
  const selectAllEvents = () => {
    setFormData({
      ...formData,
      events: [...AVAILABLE_EVENTS]
    });
  };
  const clearAllEvents = () => {
    setFormData({
      ...formData,
      events: []
    });
  };
  const statusBadge = (status: string) => status === 'Active' ? styles.badgeActive : styles.badgeInactive;
  const typeBadge = (type: string) => type === 'Global' ? styles.badgeTypeGlobal : styles.badgeTypeOther;
  // ─── Add / Edit View ───
  if (viewMode === 'add' || viewMode === 'edit') {
    const isEdit = viewMode === 'edit';
    return <div className="max-w-5xl mx-auto p-6 space-y-6" data-id="element-4539">
        <div className={`${styles.card} p-4 flex items-center gap-3`} data-id="element-4540">
          <button onClick={() => {
          setViewMode('list');
          resetForm();
          setEditingWebhookId(null);
        }} className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-primary)] transition-colors rounded-md hover:bg-[var(--colors-neutral-100)]" data-id="element-4541">
            <ArrowLeft className="w-5 h-5" data-id="element-4542" />
          </button>
          <h1 className={`text-lg font-medium ${styles.heading}`} data-id="element-4543">
            {isEdit ? 'Edit Webhook' : 'Add Webhook'}
          </h1>
        </div>

        <div className={styles.card} data-id="element-4544">
          <div className="p-6 space-y-6" data-id="element-4545">
            <div data-id="element-4546">
              <label className={styles.label} data-id="element-4547">
                Name{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-4548">*</span>
              </label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className={styles.input} placeholder="e.g, Payment Notifications" data-id="element-4549" />
            </div>

            <div data-id="element-4550">
              <label className={styles.label} data-id="element-4551">
                Webhook URL{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-4552">*</span>
              </label>
              <input type="url" value={formData.url} onChange={e => setFormData({
              ...formData,
              url: e.target.value
            })} className={styles.input} placeholder="https://example.com/webhook" data-id="element-4553" />
            </div>

            <div data-id="element-4554">
              <label className={styles.label} data-id="element-4555">
                Type{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-4556">*</span>
              </label>
              <select value={formData.type} onChange={e => setFormData({
              ...formData,
              type: e.target.value
            })} className={styles.input} data-id="element-4557">
                <option value="Global" data-id="element-4558">Global</option>
                <option value="Transaction-level" data-id="element-4559">Transaction-level</option>
                <option value="Payment" data-id="element-4560">Payment</option>
                <option value="Refund" data-id="element-4561">Refund</option>
                <option value="Dispute" data-id="element-4562">Dispute</option>
                <option value="All" data-id="element-4563">All</option>
              </select>
            </div>

            <div data-id="element-4564">
              <label className={styles.label} data-id="element-4565">
                Events{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-4566">*</span>
              </label>
              <div className="border border-[var(--colors-border-default)] rounded-md p-1.5 min-h-[42px] flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-[var(--brand-primaryring)] focus-within:border-[var(--colors-border-focus)] relative bg-[var(--colors-background-card)] pr-8" data-id="element-4567">
                {formData.events.map(event => <span key={event} className={`${styles.eventChip} gap-1`} data-id="element-4568">
                    {event}
                    <button onClick={() => toggleEvent(event)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] focus:outline-none" data-id="element-4569">
                      <X className="w-3.5 h-3.5" data-id="element-4570" />
                    </button>
                  </span>)}
                <select className="flex-1 min-w-[150px] border-none focus:ring-0 text-sm py-1 bg-transparent text-[var(--colors-text-tertiary)] outline-none" onChange={e => {
                if (e.target.value) {
                  toggleEvent(e.target.value);
                  e.target.value = '';
                }
              }} value="" data-id="element-4571">
                  <option value="" disabled data-id="element-4572">
                    -- Select one --
                  </option>
                  {AVAILABLE_EVENTS.filter(e => !formData.events.includes(e)).map(event => <option key={event} value={event} data-id="element-4573">
                      {event}
                    </option>)}
                </select>
                {formData.events.length > 0 && <button onClick={clearAllEvents} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-4574">
                    <X className="w-4 h-4" data-id="element-4575" />
                  </button>}
              </div>
              <div className="mt-2" data-id="element-4576">
                <button onClick={selectAllEvents} className={`${styles.btnGhost} px-3 py-1.5`} data-id="element-4577">
                  Select all
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[var(--colors-border-light)] flex justify-end" data-id="element-4578">
            <button onClick={isEdit ? handleSaveEdit : handleAddWebhook} disabled={!formData.name || !formData.url || formData.events.length === 0} className={`px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${isEdit ? styles.btnDanger : styles.btnPrimary}`} data-id="element-4579">
              {isEdit ? 'Save changes' : 'Add webhook'}
            </button>
          </div>
        </div>
      </div>;
  }
  // ─── List View ───
  return <div className="max-w-7xl mx-auto p-6 space-y-6" data-id="element-4580">
      {/* Header */}
      <div className={`${styles.card} p-6 flex justify-between items-center`} data-id="element-4581">
        <div data-id="element-4582">
          <h1 className={`text-xl ${styles.heading}`} data-id="element-4583">Webhooks</h1>
          <p className={`${styles.subtext} mt-1`} data-id="element-4584">
            Manage webhook endpoints for receiving real-time event notifications
          </p>
        </div>
        <button onClick={openAddView} className={`${styles.btnPrimary} px-4 py-2`} data-id="element-4585">
          Add Webhook
        </button>
      </div>

      {/* Global Webhook */}
      <div className={styles.card} data-id="element-4586">
        <div className="p-6 border-b border-[var(--colors-border-light)]" data-id="element-4587">
          <h2 className={`text-base font-medium ${styles.heading}`} data-id="element-4588">
            Global Webhook
          </h2>
          <p className={`${styles.subtext} mt-1`} data-id="element-4589">
            Configure a global webhook URL to receive events for all
            transactions.
          </p>
          <div className="mt-6" data-id="element-4590">
            <label className={styles.label} data-id="element-4591">Webhook URL</label>
            <input type="text" value={globalWebhookUrl} onChange={e => setGlobalWebhookUrl(e.target.value)} placeholder="https://example.com/webhook" className={styles.input} data-id="element-4592" />
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end gap-3 rounded-b-lg" data-id="element-4593">
          <button onClick={handleGlobalUpdate} className={`${styles.btnPrimary} px-4 py-2 min-w-[80px] flex justify-center items-center`} data-id="element-4594">
            {globalUpdateStatus === 'success' ? <span className="flex items-center gap-1" data-id="element-4595">
                <Check className="w-4 h-4" data-id="element-4596" /> Updated!
              </span> : 'Update'}
          </button>
          <button onClick={handleGlobalRemove} className={`${styles.btnDanger} px-4 py-2`} data-id="element-4597">
            Remove
          </button>
        </div>
      </div>

      {/* Configured Webhooks */}
      <div className={styles.card} data-id="element-4598">
        <div className="p-6 border-b border-[var(--colors-border-light)]" data-id="element-4599">
          <h2 className={`text-base font-medium ${styles.heading}`} data-id="element-4600">
            Configured Webhooks
          </h2>
        </div>

        <div className="overflow-x-auto" data-id="element-4601">
          <table className="w-full text-left text-sm text-[var(--colors-text-secondary)]" data-id="element-4602">
            <thead className="border-b border-[var(--colors-border-light)]" data-id="element-4603">
              <tr data-id="element-4604">
                <th className={styles.th} data-id="element-4605">
                  <div className="flex items-center gap-2" data-id="element-4606">
                    Name & URL{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-4607" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-4608">
                  <div className="flex items-center gap-2" data-id="element-4609">
                    Type{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-4610" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-4611">
                  <div className="flex items-center gap-2" data-id="element-4612">
                    Status{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-4613" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-4614">
                  <div className="flex items-center gap-2" data-id="element-4615">
                    Events{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-4616" />
                  </div>
                </th>
                <th className="px-6 py-3 font-medium text-[var(--colors-text-secondary)] text-right" data-id="element-4617">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4618">
              {webhooks.length === 0 ? <tr data-id="element-4619">
                  <td colSpan={5} className="px-6 py-24 text-center" data-id="element-4620">
                    <div className="flex flex-col items-center justify-center" data-id="element-4621">
                      <div className="w-12 h-12 bg-[var(--colors-neutral-50)] rounded-full flex items-center justify-center mb-4" data-id="element-4622">
                        <Clock className="w-6 h-6 text-[var(--colors-text-tertiary)]" data-id="element-4623" />
                      </div>
                      <h3 className={`text-base font-medium ${styles.heading} mb-1`} data-id="element-4624">
                        No webhooks
                      </h3>
                      <p className={styles.subtext} data-id="element-4625">
                        Webhooks you add will appear here.
                      </p>
                    </div>
                  </td>
                </tr> : webhooks.map(webhook => <tr key={webhook.id} className="border-b border-[var(--colors-neutral-100)] last:border-0 hover:bg-[var(--colors-neutral-50)]" data-id="element-4626">
                    <td className="px-6 py-4" data-id="element-4627">
                      <div className="font-medium text-[var(--colors-text-primary)]" data-id="element-4628">
                        {webhook.name || '—'}
                      </div>
                      <div className="text-[var(--colors-text-tertiary)] text-xs mt-0.5 font-mono" data-id="element-4629">
                        {webhook.url}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4630">
                      <span className={`${styles.badgeBase} ${typeBadge(webhook.type)}`} data-id="element-4631">
                        {webhook.type}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-4632">
                      <span className={`${styles.badgeBase} ${statusBadge(webhook.status)}`} data-id="element-4633">
                        {webhook.status}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-4634">
                      <div className="flex flex-wrap gap-1" data-id="element-4635">
                        {webhook.events.map(event => <span key={event} className={styles.eventChip} data-id="element-4636">
                            {event}
                          </span>)}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4637">
                      <div className="flex items-center justify-end gap-1" data-id="element-4638">
                        <button onClick={() => openEditView(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] transition-colors" title="Edit" data-id="element-4639">
                          <Pencil className="w-4 h-4" data-id="element-4640" />
                        </button>
                        <button onClick={() => setDeleteModalWebhook(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-error-light)] text-[var(--colors-error-default)] hover:text-[var(--colors-error-dark)] transition-colors" title="Delete" data-id="element-4641">
                          <Trash2 className="w-4 h-4" data-id="element-4642" />
                        </button>
                        <button onClick={() => setInfoModalWebhook(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-info-light)] text-[var(--colors-info-default)] hover:text-[var(--colors-info-dark)] transition-colors" title="Details" data-id="element-4643">
                          <Info className="w-4 h-4" data-id="element-4644" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-[var(--colors-border-light)] bg-[var(--colors-neutral-50)] flex items-center justify-between rounded-b-lg" data-id="element-4645">
          <div className="text-sm text-[var(--colors-text-tertiary)] font-medium" data-id="element-4646">
            {webhooks.length === 0 ? 'No entries' : `Showing 1-${webhooks.length} of ${webhooks.length} ${webhooks.length === 1 ? 'entry' : 'entries'}`}
          </div>
          <div className="flex items-center gap-4" data-id="element-4647">
            <div className="flex items-center gap-2" data-id="element-4648">
              <span className="text-sm text-[var(--colors-text-tertiary)] font-medium" data-id="element-4649">
                Show
              </span>
              <select className="border border-[var(--colors-border-default)] rounded-md text-sm py-1.5 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] bg-[var(--colors-background-card)]" data-id="element-4650">
                <option data-id="element-4651">25</option>
                <option data-id="element-4652">50</option>
                <option data-id="element-4653">100</option>
              </select>
            </div>
            <div className="flex items-center gap-1" data-id="element-4654">
              <button className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] disabled:opacity-50" disabled data-id="element-4655">
                <ChevronLeft className="w-4 h-4" data-id="element-4656" />
              </button>
              {webhooks.length > 0 && <span className="text-sm font-medium text-[var(--colors-text-secondary)] px-2" data-id="element-4657">
                  1
                </span>}
              <button className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] disabled:opacity-50" disabled data-id="element-4658">
                <ChevronRight className="w-4 h-4" data-id="element-4659" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalWebhook && <div className={styles.overlay} data-id="element-4660">
          <div className="bg-[var(--colors-background-card)] rounded-lg shadow-[var(--shadows-xl)] w-full max-w-md overflow-hidden" data-id="element-4661">
            <div className="flex justify-end p-4 pb-0" data-id="element-4662">
              <button onClick={() => setDeleteModalWebhook(null)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-4663">
                <X className="w-5 h-5" data-id="element-4664" />
              </button>
            </div>
            <div className="px-6 pb-6 pt-2 text-center" data-id="element-4665">
              <h3 className={`text-xl ${styles.heading} mb-2`} data-id="element-4666">
                Delete Webhook
              </h3>
              <p className={`${styles.subtext} leading-relaxed`} data-id="element-4667">
                Are you sure you want to delete Webhook?
                <br data-id="element-4668" />
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-center gap-4" data-id="element-4669">
              <button onClick={() => setDeleteModalWebhook(null)} className="text-sm font-medium text-[var(--colors-text-secondary)] hover:text-[var(--colors-text-primary)] transition-colors" data-id="element-4670">
                No, go back
              </button>
              <button onClick={() => handleDeleteWebhook(deleteModalWebhook)} className={`${styles.btnDanger} px-4 py-2`} data-id="element-4671">
                Yes, delete Webhook
              </button>
            </div>
          </div>
        </div>}

      {/* Info / Detail Modal */}
      {infoModalWebhook && <div className={styles.overlay} data-id="element-4672">
          <div className="bg-[var(--colors-background-card)] rounded-lg shadow-[var(--shadows-xl)] w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" data-id="element-4673">
            <div className="flex justify-end p-4 pb-0 flex-shrink-0" data-id="element-4674">
              <button onClick={() => setInfoModalWebhook(null)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-4675">
                <X className="w-5 h-5" data-id="element-4676" />
              </button>
            </div>
            <div className="px-6 pb-6 overflow-y-auto" data-id="element-4677">
              <table className="w-full text-sm" data-id="element-4678">
                <tbody className="divide-y divide-[var(--colors-neutral-100)]" data-id="element-4679">
                  <tr data-id="element-4680">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top w-36" data-id="element-4681">
                      Webhook ID
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)] font-mono text-xs" data-id="element-4682">
                      {infoModalWebhook.id}
                    </td>
                  </tr>
                  <tr data-id="element-4683">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4684">
                      Name
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-4685">
                      {infoModalWebhook.name || '—'}
                    </td>
                  </tr>
                  <tr data-id="element-4686">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4687">
                      Endpoint URL
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)] break-all" data-id="element-4688">
                      {infoModalWebhook.url}
                    </td>
                  </tr>
                  <tr data-id="element-4689">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4690">
                      Type
                    </td>
                    <td className="py-3" data-id="element-4691">
                      <span className={`${styles.badgeBase} ${typeBadge(infoModalWebhook.type)}`} data-id="element-4692">
                        {infoModalWebhook.type}
                      </span>
                    </td>
                  </tr>
                  <tr data-id="element-4693">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4694">
                      Status
                    </td>
                    <td className="py-3" data-id="element-4695">
                      <span className={`${styles.badgeBase} ${statusBadge(infoModalWebhook.status)}`} data-id="element-4696">
                        {infoModalWebhook.status}
                      </span>
                    </td>
                  </tr>
                  <tr data-id="element-4697">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4698">
                      Events
                    </td>
                    <td className="py-3" data-id="element-4699">
                      <p className="text-[var(--colors-text-tertiary)] text-xs mb-2" data-id="element-4700">
                        This webhook receives all event types.
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-4701">
                        {infoModalWebhook.events.map(event => <span key={event} className={styles.eventChip} data-id="element-4702">
                            {event}
                          </span>)}
                      </div>
                    </td>
                  </tr>
                  <tr data-id="element-4703">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4704">
                      Created
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-4705">
                      {formatDate(infoModalWebhook.createdAt)}
                    </td>
                  </tr>
                  <tr data-id="element-4706">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-4707">
                      Last updated
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-4708">
                      {formatDate(infoModalWebhook.updatedAt)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Activity Log */}
              <div className="mt-6 border-t border-[var(--colors-border-light)] pt-4" data-id="element-4709">
                <h4 className={`text-sm ${styles.heading} mb-3`} data-id="element-4710">
                  Activity Log
                </h4>
                {infoModalWebhook.activity.length === 0 ? <p className={styles.subtext} data-id="element-4711">No activity recorded.</p> : <div className="space-y-0" data-id="element-4712">
                    {infoModalWebhook.activity.map((entry, idx) => <div key={idx} className="flex items-start gap-3 py-2.5 relative" data-id="element-4713">
                        {idx < infoModalWebhook.activity.length - 1 && <div className="absolute left-[7px] top-[22px] bottom-0 w-px bg-[var(--colors-border-light)]" data-id="element-4714" />}
                        <div className="w-[15px] h-[15px] rounded-full bg-[var(--colors-primary-100)] border-2 border-[var(--brand-primary)] flex-shrink-0 mt-0.5 relative z-10" data-id="element-4715" />
                        <div className="flex-1 min-w-0" data-id="element-4716">
                          <p className="text-sm text-[var(--colors-text-primary)] font-medium" data-id="element-4717">
                            {entry.action}
                          </p>
                          <p className="text-xs text-[var(--colors-text-tertiary)] mt-0.5" data-id="element-4718">
                            by {entry.user} · {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}